import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowLeft } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import {
  getFirm, createFirmAssignment, listAssignmentSubmissions, reviewFirmSubmission,
} from "@/lib/api/lxp-firms.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/firms/$firmId")({
  head: () => ({ meta: [{ title: "Firm workspace | SA LexPractiq" }] }),
  component: FirmDetail,
});

function FirmDetail() {
  const { firmId } = Route.useParams();
  const getFn = useServerFn(getFirm);
  const createAFn = useServerFn(createFirmAssignment);
  const subsFn = useServerFn(listAssignmentSubmissions);
  const reviewFn = useServerFn(reviewFirmSubmission);

  const [data, setData] = useState<any>({ firm: null, assignments: [], members: [] });
  const [selected, setSelected] = useState<any | null>(null);
  const [subs, setSubs] = useState<any[]>([]);
  const [form, setForm] = useState({ title: "", brief: "", practiceArea: "", deadline: "", openToPublic: true });
  const [show, setShow] = useState(false);

  async function reload() { setData(await getFn({ data: { firmId } })); }
  useEffect(() => { reload(); }, [firmId]);

  async function pickAssignment(a: any) {
    setSelected(a);
    try { const r = await subsFn({ data: { assignmentId: a.id } }); setSubs(r.submissions); }
    catch (e: any) { toast.error(e?.message); }
  }

  async function createA() {
    if (form.title.length < 4 || form.brief.length < 10) return toast.error("Title and brief required");
    try {
      await createAFn({ data: {
        firmId, title: form.title, brief: form.brief,
        practiceArea: form.practiceArea || undefined,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : undefined,
        openToPublic: form.openToPublic,
      } });
      toast.success("Assignment published");
      setShow(false); setForm({ title: "", brief: "", practiceArea: "", deadline: "", openToPublic: true });
      await reload();
    } catch (e: any) { toast.error(e?.message); }
  }

  async function reviewOne(id: string, score: number, feedback: string) {
    try { await reviewFn({ data: { id, score, feedback } }); toast.success("Review saved"); if (selected) pickAssignment(selected); }
    catch (e: any) { toast.error(e?.message); }
  }

  const firm = data.firm;
  if (!firm) return <LxpLayout><div className="lxp-container" style={{ padding: "2rem 1.25rem" }}><p>Loading…</p></div></LxpLayout>;

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <Link to="/lexpractiq/firms" className="lxp-btn-ghost" style={{ marginBottom: "1rem", display: "inline-flex" }}><ArrowLeft size={14} /> All firms</Link>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <h1 style={{ margin: 0 }}>{firm.name}</h1>
            {firm.description && <p className="lxp-muted" style={{ margin: "0.35rem 0" }}>{firm.description}</p>}
            {firm.website && <a className="lxp-btn-ghost" href={firm.website} target="_blank" rel="noreferrer" style={{ fontSize: "0.8rem" }}>{firm.website}</a>}
          </div>
          <button className="lxp-btn" onClick={() => setShow((s) => !s)}><Plus size={14} /> New assignment</button>
        </div>

        {show && (
          <div className="lxp-card" style={{ marginBottom: "1.25rem" }}>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <input className="lxp-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <textarea className="lxp-textarea" rows={5} placeholder="Brief / instructions" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                <input className="lxp-input" placeholder="Practice area" value={form.practiceArea} onChange={(e) => setForm({ ...form, practiceArea: e.target.value })} />
                <input className="lxp-input" type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input type="checkbox" checked={form.openToPublic} onChange={(e) => setForm({ ...form, openToPublic: e.target.checked })} />
                Open to all candidates
              </label>
              <div><button className="lxp-btn" onClick={createA}>Publish</button></div>
            </div>
          </div>
        )}

        <h3>Assignments</h3>
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {data.assignments.map((a: any) => (
            <div key={a.id} className="lxp-card">
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "start" }}>
                <div>
                  <strong>{a.title}</strong>
                  <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                    {a.practice_area || ""} · {a.status} {a.deadline ? `· Due ${new Date(a.deadline).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <button className="lxp-btn-ghost" onClick={() => pickAssignment(a)}>Review submissions</button>
              </div>
              {selected?.id === a.id && (
                <div style={{ marginTop: "0.75rem", borderTop: "1px solid var(--lxp-border)", paddingTop: "0.75rem" }}>
                  <strong style={{ fontSize: "0.9rem" }}>Submissions ({subs.length})</strong>
                  {subs.length === 0 && <p className="lxp-muted" style={{ fontSize: "0.8rem" }}>No submissions yet.</p>}
                  {subs.map((s) => <SubmissionRow key={s.id} sub={s} onReview={reviewOne} />)}
                </div>
              )}
            </div>
          ))}
          {data.assignments.length === 0 && <p className="lxp-muted">No assignments yet.</p>}
        </div>
      </div>
    </LxpLayout>
  );
}

function SubmissionRow({ sub, onReview }: { sub: any; onReview: (id: string, score: number, feedback: string) => void }) {
  const [score, setScore] = useState<number>(sub.reviewer_score ?? 70);
  const [fb, setFb] = useState<string>(sub.reviewer_feedback ?? "");
  return (
    <div className="lxp-card" style={{ marginTop: "0.5rem", background: "var(--lxp-bg)" }}>
      <div className="lxp-muted" style={{ fontSize: "0.75rem" }}>Candidate: {sub.user_id.slice(0, 8)}… · {sub.status}</div>
      <div style={{ marginTop: "0.4rem", fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>{sub.body_html}</div>
      <div style={{ display: "grid", gap: "0.4rem", marginTop: "0.5rem", gridTemplateColumns: "80px 1fr auto" }}>
        <input type="number" min={0} max={100} className="lxp-input" value={score} onChange={(e) => setScore(Number(e.target.value))} />
        <input className="lxp-input" placeholder="Feedback" value={fb} onChange={(e) => setFb(e.target.value)} />
        <button className="lxp-btn" onClick={() => onReview(sub.id, score, fb)}>Save</button>
      </div>
    </div>
  );
}
